import PropTypes from 'prop-types';
import React from 'react';

import {getNewBarcodeColumn} from 'bulk-import/storables/StorableColumnsUtil';
import {Registry, REGISTRY_SHAPE} from 'constants/RegistryConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import BoxSchemaSelector from 'samples/BoxSchemaSelector';
import * as ModalContainer from 'ui-library/ModalContainer';

import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import StorableImportSettingsModalPane from './panes/StorableImportSettingsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  STORABLE_BASE_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';
import * as SpreadsheetBulkImportUtil from './SpreadsheetBulkImportUtil';

export interface BulkUpdateBoxesFlowProps {
  initialRegistry: Registry | null;
  onRequestClose: () => void;
  actionOrigin: ActionOrigin;
}

class BulkUpdateBoxesFlow extends React.Component<BulkUpdateBoxesFlowProps> {
  static propTypes = {
    initialRegistry: REGISTRY_SHAPE,
    onRequestClose: PropTypes.func.isRequired,
  };

  static open(props: Omit<BulkUpdateBoxesFlowProps, 'onRequestClose'>): void {
    ModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkUpdateBoxesFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  _getPanes = (): Array<Pane> => [
    {
      paneKey: PaneKey.SET_STORABLE_IMPORT_SETTINGS,
      configuration: StorableImportSettingsModalPane.configure({
        schemaSelectorClass: BoxSchemaSelector,
        schemaIdAccessorKey: 'boxSchemaId',
        showProjectSelector: false,
      }),
    },
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({
        getExampleImageURL: () => '/static/img/spreadsheet-import-example-create-boxes.png',
      }),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        getValidColumnTypes: (): Array<COLUMN_TYPE> => [
          ...STORABLE_BASE_COLUMN_TYPES,
          COLUMN_TYPE.BOX_SCHEMA_FIELD,
          ...getNewBarcodeColumn(),
          COLUMN_TYPE.STUDY_LABELS,
          COLUMN_TYPE.IGNORE,
        ],
        getRequiredColumnsError(
          columns: Array<SpreadsheetColumn>,
          {selectedSchemaIds}: {selectedSchemaIds: SelectedSchemaIds}
        ): string | null {
          const requiredColumnTypes = [COLUMN_TYPE.BARCODE];
          const requiredFieldError = SpreadsheetBulkImportUtil.getRequiredFieldError(
            requiredColumnTypes,
            columns,
            selectedSchemaIds,
            IMPORT_TYPE.UPDATE_BOXES
          );
          if (requiredFieldError) {
            return requiredFieldError;
          }
          if (!columns.some(({type}) => type !== COLUMN_TYPE.BARCODE)) {
            return 'At least one other field is required.';
          }
          return null;
        },
        onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForUpdateBoxes,
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: true,
        enableSkipErrors: true,
      }),
    },
  ];

  render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="box"
        importType={IMPORT_TYPE.UPDATE_BOXES}
        initialSettings={{initialRegistry: this.props.initialRegistry}}
        onImportFromSpreadsheet={SpreadsheetBulkImportActions.updateBoxesFromSpreadsheetWithDataMods}
        analyticsMetadata={{actionOrigin: this.props.actionOrigin}}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Bulk update boxes"
      />
    );
  }
}

export default BulkUpdateBoxesFlow;
